import { gql, useQuery } from '@apollo/client';
import { Box, Image, useDisclosure } from '@chakra-ui/react';
import * as React from 'react';

import { ResourceCarousel } from '@/components/ResourceCarousel';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { Carousel } from '@/imports/ui/components/Carousel';

import {
  Resources_descriptiveResources,
  Resources_descriptiveResourcesVariables,
} from './graphql-types/Resources_descriptiveResources';

const ORGANIZATION_RESOURCES = gql`
  query Resources_descriptiveResources($input: SingleOrganizationInput!) {
    organization(input: $input) {
      result {
        _id
        descriptiveResources {
          ... on Organization_DescriptiveResource_Image {
            imageId
            thumbnailImage: imageProxy(input: { h: 288 })
            originalSizeImage: imageProxy(input: {})
          }
          ... on Organization_DescriptiveResource_Video {
            videoId
            videoFile {
              _id
              s3DownloadUrl
              hlsVideoStreamUrl
              thumbnailImageUrl(input: { height: 288 })
            }
          }
        }
      }
    }
  }
`;

export const Resources: React.FC<{
  organizationId: string;
  alt: string;
}> = ({ organizationId, alt }) => {
  const { data, error, startPolling, stopPolling } = useQuery<
    Resources_descriptiveResources,
    Resources_descriptiveResourcesVariables
  >(ORGANIZATION_RESOURCES, {
    variables: {
      input: {
        id: organizationId,
      },
    },
  });

  const resources = data?.organization?.result?.descriptiveResources;

  React.useEffect(() => {
    const hasProcessingVideos = resources?.some(
      (resource) =>
        'videoId' in resource &&
        resource?.videoId &&
        !resource?.videoFile?.hlsVideoStreamUrl
    );

    if (!error && hasProcessingVideos) {
      startPolling(10000);
    } else {
      stopPolling();
    }
  }, [resources, error, startPolling, stopPolling]);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [currentSlide, setCurrentSlide] = React.useState(0);

  if (!resources?.length) {
    return null;
  }

  return (
    <>
      <Box w="full">
        <Box mx="-2">
          <Carousel numItems={3} buttonSpacing={4}>
            {resources.map((resource, idx) => {
              const key =
                'imageId' in resource ? resource?.imageId : resource?.videoId;
              const src =
                ('imageId' in resource
                  ? resource?.thumbnailImage
                  : resource?.videoFile?.thumbnailImageUrl) ?? undefined;

              return (
                <Box key={key} px="2">
                  <Image
                    alt={alt}
                    src={src}
                    w="full"
                    sx={{ aspectRatio: '4/3' }}
                    objectFit="cover"
                    borderRadius="md"
                    cursor="pointer"
                    fallback={
                      <ImageFallback
                        aspectRatio="4/3"
                        borderRadius="md"
                        bg="secondary.500"
                        onClick={() => {
                          setCurrentSlide(idx);
                          onOpen();
                        }}
                      />
                    }
                    onClick={() => {
                      setCurrentSlide(idx);
                      onOpen();
                    }}
                  />
                </Box>
              );
            })}
          </Carousel>
        </Box>
      </Box>

      <ResourceCarousel
        isOpen={isOpen}
        onClose={onClose}
        slides={resources.map((resource) =>
          'imageId' in resource
            ? {
                id: resource.imageId,
                imageProxy: resource.originalSizeImage ?? undefined,
              }
            : {
                id: resource.videoId,
                imageProxy: resource.videoFile?.thumbnailImageUrl ?? undefined,
                videoUrl: resource.videoFile?.hlsVideoStreamUrl ?? undefined,
              }
        )}
        currentSlide={currentSlide}
      />
    </>
  );
};
